import { Injectable } from '@angular/core';
import { KeywordToResourceResolver, SimpleChipExpression } from '@nrwl/query-emitter';
import { EntityCacheProvider, IEntityCache, ValuesExchangeProvider } from '@nrwl/common-lib';
import { clone, find, get, toUpper, trim } from 'lodash';
import { Unsubscribable } from 'rxjs';

const dataModels: IDataModels = require('./json/data.models.json');

interface IDataModels {
  combinedflow: IDataModel[];
  kpiName: IKpiDataModel[];
}

/**
 * The order of the fields in 'matches' and 'idmatches' array must be precisely
 * the same. (n-th keyword from matches[] array will be translated to n-th keyword from idmatches[]).
 *
 * Use cases:
 * a) Some matches are simply replacing one field name with another (eg, 'app' is replaced with
 * another DB field 'appresourceid').
 * b) Some other matches will replace one alias with another alias (eg, 'site_src' will be replaced with
 * site_resourceid_src).
 *
 * Just a gentle reminder - you can't invent aliases on your own - they must follow naming
 * convention, agreed with backend.
 */
interface IDataModel {
  quick_prefix: string;
  pairs: IFieldPair[];
  entity?: string;
  translateValues?: boolean;
}

interface IFieldPair {
  match: string;
  idmatch: string;
}

interface IKpiDataModel {
  name: string;
  rules: IDataModel[];
}

export interface IKeywordAsDbKeyword {
  originalKeyword: string;
  dbKeyword: string;
  translateValues: boolean;
  entity?: string;
}

interface IKeywordAsUserKeyword {
  entity?: string;
  userKeyword: string;
}

interface IKeywordWithCache {
  cache: IEntityCache;
  dbKeyword: string;
  userKeyword: string;
}

@Injectable()
export class PtcKeywordToResourceResolver extends KeywordToResourceResolver {
  queryBuilder;
  registeredDataModels: IDataModels;
  // pairs from combinedflow
  cfDbToUser: Map<string, IKeywordAsUserKeyword>;
  // pairs from kpiNames
  kpiToDbToUser: Map<string, Map<string, IKeywordAsUserKeyword>>;
  exchangeSub: Unsubscribable;
  autoRefresh = true;

  constructor(
    private readonly entityCache: EntityCacheProvider,
    private readonly exchange: ValuesExchangeProvider
  ) {
    super();
    this.registeredDataModels = dataModels;
    this.cfDbToUser = this.parseDbKeywordsToMap(this.registeredDataModels.combinedflow);
    this.kpiToDbToUser = new Map();
    for (const kpi of this.registeredDataModels.kpiName) {
      const kpiRules = this.parseDbKeywordsToMap(kpi.rules);
      this.kpiToDbToUser.set(kpi.name, kpiRules);
    }
    this.exchangeSub = this.exchange.registerInterest('update_auto_refresh', item => {
      if (item) {
        this.autoRefresh = item.autoRefresh;
      }
    });
  }

  parseDbKeywordsToMap(rules: IDataModel[]): Map<string, IKeywordAsUserKeyword> {
    const result = new Map<string, IKeywordAsUserKeyword>();
    // right now just _combinedflow model is present.
    for (const fieldSpec of rules) {
      for (const pair of fieldSpec.pairs) {
        const dbKeyword = pair.idmatch;

        const match = result.get(dbKeyword);
        if (match) {
          // this is an error. It is assumed there is 1:1 match between UI keywords and DB keywords.
          // eg, site_src to site_resourceid_src)
          throw new Error(
            'Multiple UI keyword matches for DB keyword ' +
              dbKeyword +
              ' were detected! This is not allowed.'
          );
        }
        result.set(dbKeyword, {
          entity: fieldSpec.entity,
          userKeyword: pair.match
        });
      }
    }
    return result;
  }

  translateKeywords(kpiName: string, keywords: string[]): string[] {
    const result = [];
    for (let keyword of keywords) {
      keyword = keyword.trim();
      const translation = this.translateToDbKeyword(kpiName, keyword);
      result.push(translation.dbKeyword);
    }
    return result;
  }

  /**
   *
   * @param keyword Accepts given keyword/used on UI side and attempts to convert to
   * keyword/field name/, understood by backend services.
   */
  translateToDbKeyword(kpiName: string, keyword: string): IKeywordAsDbKeyword {
    keyword = keyword.toLowerCase();
    let translation = null;
    const definedKPI = find(this.registeredDataModels.kpiName, item => {
      return item.name === kpiName;
    });
    // kpi based translation rules are of higher priority
    if (definedKPI) {
      translation = this.attemptKeywordTranslateToDbKeyword(keyword, definedKPI.rules);
    }
    if (!translation || (!translation.entity && !translation.translateValues)) {
      translation = this.attemptKeywordTranslateToDbKeyword(
        keyword,
        this.registeredDataModels.combinedflow
      );
    }
    return translation;
  }

  attemptKeywordTranslateToDbKeyword(
    keyword: string,
    translationRules: IDataModel[]
  ): IKeywordAsDbKeyword {
    for (const fieldSpec of translationRules) {
      if (keyword.startsWith(fieldSpec.quick_prefix)) {
        for (const pair of fieldSpec.pairs) {
          if (keyword === pair.match) {
            return {
              originalKeyword: keyword,
              dbKeyword: pair.idmatch,
              translateValues:
                typeof fieldSpec.translateValues !== 'undefined' ? fieldSpec.translateValues : true,
              entity: fieldSpec.entity
            };
          }
        }
      }
    }
    return {
      originalKeyword: keyword,
      translateValues: false,
      dbKeyword: keyword
    };
  }

  // this is an entry point to call when you have specific KPI and array of chips to be treated as params of
  // that query. Will take care to convert both keywords (eg, app to appresourceid) and values (eg, 'tcp' to 111)
  translateExpressions(kpiName: string, exp: SimpleChipExpression[]): SimpleChipExpression[] {
    const result = [];
    if (exp) {
      for (const expression of exp) {
        result.push(this.translateExpression(kpiName, expression));
      }
    }
    return result;
  }

  translateExpression(kpiName: string, exp: SimpleChipExpression): SimpleChipExpression {
    const result = clone(exp);
    if (result.isGroup) {
      this.translateGroupExp(kpiName, result.group);
      return result;
    } else {
      const keywordTranslation = this.translateToDbKeyword(kpiName, exp.keyword);
      if (keywordTranslation) {
        result.keyword = keywordTranslation.dbKeyword;
        // if keyword has no info on 'entity', no translation of values will be carried on.
        // valid example: ip_src keyword is translated to ip1. While values do not need to be translated.
        if (keywordTranslation.translateValues && keywordTranslation.entity) {
          result.operands = this.translateToResourceIDs(result.operands, keywordTranslation);
        }
      }
      return result;
    }
  }

  translateToResourceIDs(operands: string[], translation: IKeywordAsDbKeyword): string[] {
    const result = [];
    if (!translation.entity) {
      // well. That's an error. If current method was invoked, it means we expect to translate
      // given values to IDs.
      throw new Error('Could not replace operands with resource IDs, entity name is not known!');
    }
    const cache = this.entityCache.findEntityCache(translation.entity);
    if (!cache) {
      throw new Error('No entity cache found for entity' + translation.entity);
    }
    for (let index = 0; index < operands.length; index++) {
      // check for special cases - eg, 'null'
      if ('NULL' === toUpper(trim(operands[index]))) {
        result.push('NULL');
        continue;
      }
      this.performSimpleNameToIDUpdate(translation.entity, cache, operands, index, result);
    }
    return result;
  }

  performSimpleNameToIDUpdate(
    entity: string,
    cache: IEntityCache,
    operands: string[],
    index: number,
    //eslint-disable-next-line
    result: any[]
  ): void {
    const entityInstances = cache.getByEscapedName(operands[index]);
    if (!entityInstances || entityInstances.length === 0) {
      console.warn(
        'Could not locate matching entity object for entity, name ',
        entity,
        operands[index],
        '. Will replace this name with -1 in query expression'
      );
      result.push('-1');
    } else {
      for (const instance of entityInstances) {
        if (typeof instance.isDeleted === 'undefined' || !instance.isDeleted) {
          result.push(instance.getId());
        }
      }
    }
  }

  //eslint-disable-next-line
  enrichResponseCallback(next: Function, timePickerUpdateNotRequested?: boolean): Function {
    return results => {
      // Auto refresh disabled and query is periodic dont call the callback funtion
      if (!this.autoRefresh && results.timerange) {
        return;
      }
      //console.log('enrichResponseCallback invoked', results, this);
      this.addResourceNamesToResponse(results);
      if (results.status === 'success') {
        if (!timePickerUpdateNotRequested && this.autoRefresh) {
          this.exchange.notifyListeners('update_time_picker', {
            fromTime: results.effectiveFromTime,
            toTime: results.effectiveToTime
          });
        }
      }
      next(results);
    };
  }

  //eslint-disable-next-line
  addResourceNamesToResponse(results: any) {
    if (!results.queries) {
      return;
    }
    for (const query of results.queries) {
      if (!query.results) {
        continue;
      }
      this.restoreOriginalKpiName(query.options);
      const fieldNames = this.determineConvertableFieldNames(query.options);
      if (Array.isArray(query.results)) {
        for (const record of query.results) {
          for (const field of fieldNames) {
            const id = record[field.dbKeyword];
            if (id) {
              if (field.cache) {
                const value = this.convertEntityIdToName(field.cache, id);
                record[field.userKeyword] = value;
                if (this.isEntityDeleted(field.cache, id)) {
                  record[`${field.userKeyword}Deleted`] = true;
                }
              } else {
                // no cache. so just insert same value under different column.
                record[field.userKeyword] = id;
              }
            }
          }
        }
      }
    }
  }

  //eslint-disable-next-line
  restoreOriginalKpiName(query) {
    // nothing to be done anymore
  }

  convertEntityIdToName(cache: IEntityCache, id: string): string {
    const entity = cache.findById(id);
    if (!entity) {
      console.error('Entity with ID ' + id + ' not found!');
      return null;
    }
    return entity.getName();
  }

  isEntityDeleted(cache: IEntityCache, id: string): boolean {
    const entity = cache.findById(id);
    if (!entity) {
      console.error('Entity with ID ' + id + ' not found!');
      return null;
    }
    return entity.isDeleted;
  }

  /**
   * Looks at given query and returns an array of keywords that can be translated to
   * user-readable values (eg appresourceid to app name).
   * @param query
   */
  //eslint-disable-next-line
  determineConvertableFieldNames(query: any): IKeywordWithCache[] {
    const kpiName = get(query, 'kpiName', undefined);

    let result = [];
    let fields = [];
    if (query && query.groupby) {
      fields = query.groupby.split(',');
    }
    fields = this.mergeWithDefaultFieldNames(kpiName, fields);
    result = this.determineConvertableFieldNamesByKeywords(kpiName, fields);

    return result;
  }

  //eslint-disable-next-line
  mergeWithDefaultFieldNames(kpiName: string, fields: any): string[] {
    const knownPairs = this.kpiToDbToUser.get(kpiName);
    if (!knownPairs) {
      return fields;
    }
    const uniqueNames = new Set<string>();
    for (const name of fields) {
      uniqueNames.add(name);
    }
    knownPairs.forEach((value, key) => {
      uniqueNames.add(key);
    });
    return Array.from(uniqueNames);
  }

  findKeywordMatch(kpiName: string, fieldName: string): IKeywordAsUserKeyword {
    let match: IKeywordAsUserKeyword = null;
    const priorityMap = this.kpiToDbToUser.get(kpiName);
    if (priorityMap) {
      match = priorityMap.get(fieldName);
    }
    if (!match) {
      match = this.cfDbToUser.get(fieldName);
    }
    return match;
  }

  determineConvertableFieldNamesByKeywords(kpiName: string, names: string[]): IKeywordWithCache[] {
    const result = [];

    if (names && names.length > 0) {
      for (const field of names) {
        const match = this.findKeywordMatch(kpiName, field);
        if (match) {
          let cache = null;
          if (match.entity) {
            cache = this.entityCache.findEntityCache(match.entity);
            if (!cache) {
              console.error('Could not locate cache for entity ', match.entity);
              continue;
            }
          } else {
            // some keywords have no entity cache. but still are willing to participate in conversion between UI name and DB name.
            // example: ip_src was translated to ip1, then response with ip1 = xxx must be enriched by adding field ip_src=xxx
            // such cases will have no cache.
          }
          result.push({
            cache: cache,
            userKeyword: match.userKeyword,
            dbKeyword: field
          });
        }
      }
    }
    return result;
  }

  //eslint-disable-next-line
  translateKpiName(kpiName: string, groupby: string): string {
    // nothing to be done right now
    return kpiName;
  }

  private translateGroupExp(kpiName, members) {
    members.forEach(member => {
      if (member.isGroup) {
        this.translateGroupExp(kpiName, member.group);
      } else {
        this.translateExp(kpiName, member);
      }
    });
  }

  private translateExp(kpiName, member) {
    const keywordTranslation = this.translateToDbKeyword(kpiName, member.keyword);
    if (keywordTranslation) {
      member.keyword = keywordTranslation.dbKeyword;
      // if keyword has no info on 'entity', no translation of values will be carried on.
      // valid example: ip_src keyword is translated to ip1. While values do not need to be translated.
      if (keywordTranslation.translateValues && keywordTranslation.entity) {
        member.operands = this.translateToResourceIDs(member.operands, keywordTranslation);
      }
    }
  }
}
